import { useMemo } from 'react'
import { TargetPlane } from '@scene'
import { reduce } from 'd3'
import { v4 as uuid } from 'uuid'
import { DEFAULT_SLAB_LIKE_SETTINGS } from '@editor/pages/StructuralPlanning/components/PlanSettingsForm/utils'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { useEditElementStore, useModelStore } from '@editorStores'
import { useGuidToElement } from '@editorHooks'
import {
  SnapTargetElementType,
  useDefault2DSnapConfigForElementType,
} from 'src/components/pages/Editor/hooks/useDefaultSnapConfigForElementType'
import { calculateBeamGeometry } from 'src/utils'
import RectangularShapeDrawer2D from '../RectangularShapeDrawer2D'
import { NUM_DECIMALS_ROUND_VERTICAL_ROOF_SLAB_DRAWER_2D } from './constants'

interface Props {
  storey: string
}

const VerticalRoofDrawer2D = ({ storey }: Props) => {
  const model = useModelStore(state => state.model)
  const setActiveElement = useEditElementStore(state => state.setActiveElement)
  const addVerticalRoofSlab = useModelStore(state => state.addVerticalRoofSlab)
  const elements = useGuidToElement()
  const drawRoofOnElement = useEditElementStore(state => state.drawRoofOnElement)

  // artificial id used for storey
  const storeyId = useMemo(() => uuid(), [storey])

  const storeyPoints = useMemo(() => {
    const upperBoundary = model.storey_boundaries[storey][1]
    return [
      new ImmutableVector3(-100, -100, upperBoundary),
      new ImmutableVector3(-100, 100, upperBoundary),
      new ImmutableVector3(100, 100, upperBoundary),
      new ImmutableVector3(100, -100, upperBoundary),
    ]
  }, [model.storey_boundaries, storey])

  const storeyTarget = useMemo(() => {
    return {
      [storeyId]: storeyPoints,
    }
  }, [model.storey_boundaries, storeyId])

  const targetElements = useMemo(
    () =>
      reduce(
        [...model.roof_slabs, ...model.slabs],
        (collector, element) => ({ ...collector, [element.guid]: element.shape.points }),
        {} as Record<string, ImmutableVector3[]>,
      ),
    [model],
  )
  useDefault2DSnapConfigForElementType(SnapTargetElementType.VERTICAL_ROOF_SLABS)

  return (
    <>
      <RectangularShapeDrawer2D
        roundNumDecimalsXY={NUM_DECIMALS_ROUND_VERTICAL_ROOF_SLAB_DRAWER_2D}
        targetElements={drawRoofOnElement ? targetElements : storeyTarget}
        onRectangleDrawn={(points, guid) => {
          const roofGuid = uuid()
          const beamGuid = uuid()
          const relativePosition = 0.5

          // Calculate beam geometry using utility function
          const { start: startPoint, end: endPoint } = calculateBeamGeometry(
            points,
            relativePosition,
          )

          const roof: VerticalRoofSlab = {
            guid: roofGuid,
            shape: { points: points, shape_type: 'Rectangular' },
            storey: drawRoofOnElement ? elements[guid].storey : storey,
            element_type: 'vertical_roof_slabs' as ElementTypes,
            is_local: true,
            element_guid: beamGuid,
            ...DEFAULT_SLAB_LIKE_SETTINGS,
            beam: {
              guid: beamGuid,
              element_guid: beamGuid,
              slab_guid: roofGuid,
              relative_position: relativePosition,
              element_type: 'roof_slab_beams' as ElementTypes,
              shape: {
                start: startPoint,
                end: endPoint,
              },
              storey: drawRoofOnElement ? elements[guid].storey : storey,
              is_local: true,
            } as SlabBeam,
            step_size: 0,
          }

          setActiveElement(roof.guid)
          addVerticalRoofSlab(roof)
        }}
      />
      {!drawRoofOnElement && <TargetPlane name={storeyId} points={storeyPoints} />}
    </>
  )
}

export default VerticalRoofDrawer2D
